/**
 * Module summary:
 * This page is the authenticated dashboard. It summarizes recent assessment
 * activity, computes headline metrics, and offers quick navigation into common
 * workflows such as creating assessments and reviewing reports.
 */

import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../layout/AppShell';
import ScoreGauge from '../dashboard/ScoreGauge';
import { useAuth } from '../../hooks/useAuth';
import { useAssessment } from '../../hooks/useAssessment';
import { listAssessmentsByUser, syncAssessmentsByUser } from '../../services/data';
import { getProfile } from '../../services/data';
import './pages.css';

export const HomeScreen: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState(() => (user ? listAssessmentsByUser(user.id) : []));
  useEffect(() => {
    if (!user?.id) {
      // Clear the dashboard when no user is active so stale assessment data is not shown.
      setAssessments([]);
      return;
    }
    // Show cached local data immediately, then replace it with the latest synced data from Firestore.
    setAssessments(listAssessmentsByUser(user.id));
    void syncAssessmentsByUser(user.id).then(setAssessments);
  }, [user?.id]);
  const profile = useMemo(() => getProfile(), []);

  const completed = assessments.filter((assessment) => assessment.status === 'completed').length;
  const inProgress = assessments.filter((assessment) => assessment.status !== 'completed').length;
  const total = assessments.length;

  const avgScore = assessments.filter((assessment) => assessment.score != null).length
    ? Math.round(
        // Average only scored assessments so draft records do not drag the metric down artificially.
        assessments
          .filter((assessment) => assessment.score != null)
          .reduce((sum, assessment) => sum + (assessment.score || 0), 0) /
        assessments.filter((assessment) => assessment.score != null).length
      )
    : 0;

  const lastAssessment = assessments[0];
  const firstName = (user?.displayName || 'there').split(' ')[0];
  const lastUpdatedLabel = lastAssessment?.updatedAt
    ? new Date(lastAssessment.updatedAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'No recent activity';
  const nextAction = lastAssessment
    ? lastAssessment.status === 'completed'
      ? 'Review your latest report'
      : 'Resume your latest assessment'
    : 'Start your first assessment';
  const organizationLabel = (user?.organization || profile.organization || '').trim() || 'Organization not set';

  const { assessment: assessmentDef } = useAssessment(lastAssessment?.assessmentId ?? null);
  const totalQuestions = useMemo(() => {
    if (!assessmentDef) return 0;
    return assessmentDef.categories.reduce((sum, category) => sum + category.questions.length, 0);
  }, [assessmentDef]);

  const answeredCount = useMemo(() => {
    if (!lastAssessment?.responses) return 0;
    return Object.entries(lastAssessment.responses).filter(
      ([key, value]) => !key.endsWith('Comment') && value !== '' && value !== null && value !== undefined
    ).length;
  }, [lastAssessment]);

  const latestProgress = lastAssessment?.status === 'completed'
    ? 100
    : totalQuestions > 0
      // Derive progress from answered questions when the assessment is still in progress.
      ? Math.round((answeredCount / totalQuestions) * 100)
      : 0;
  const latestProgressLabel = lastAssessment?.status === 'completed'
    ? 'Submitted'
    : totalQuestions > 0
      ? `${answeredCount} / ${totalQuestions} answered`
      : `${answeredCount} answered`;

  const heroStats = [
    { label: 'Latest activity', value: lastUpdatedLabel },
    { label: 'Account status', value: user ? 'Secure account active' : 'Waiting for login' },
    { label: 'Organization', value: organizationLabel },
  ];

  const kpis = [
    {
      label: 'Total',
      value: String(total),
      helper: total > 0 ? 'Assessments in your workspace' : 'Start your first inspection',
    },
    {
      label: 'Completed',
      value: String(completed),
      helper: completed > 0 ? 'Submitted and ready for review' : 'No completed reports yet',
    },
    {
      label: 'In Progress',
      value: String(inProgress),
      helper: inProgress > 0 ? 'Active assessments in progress' : 'Nothing currently in flight',
    },
    {
      label: 'Avg Score',
      value: avgScore > 0 ? `${avgScore}%` : '-',
      helper: avgScore > 0 ? 'Average across completed reports' : 'Appears after your first report',
    },
  ];

  const quickActions = [
    { label: 'New Assessment', href: '/create-assessment' },
    { label: 'View Reports', href: '/reports' },
    { label: 'My Profile', href: '/profile' },
    { label: 'Contact Support', href: '/contact' },
  ];

  return (
    <AppShell title="Dashboard" isDashboard>
      <div className="dashboard-page">
        <section className="dashboard-hero">
          <div className="dashboard-hero__brand">
            <img src="/Transparent-Logo.png" alt="Macha Group" />
            <span>Macha Group Security Platform</span>
          </div>
          <div className="dashboard-hero__stats">
            {heroStats.map((item) => (
              <div
                key={item.label}
                className={`dashboard-hero__stat ${item.label !== 'Latest activity' ? 'dashboard-hero__stat--secondary' : ''}`}
              >
                <span className="dashboard-hero__stat-label">{item.label}</span>
                <strong className="dashboard-hero__stat-value">{item.value}</strong>
              </div>
            ))}
          </div>

          <div className="dashboard-hero__copy">
            <p className="dashboard-hero__eyebrow">Operations Dashboard</p>
            <h1 className="dashboard-hero__title">Welcome back, {firstName}</h1>
            <p className="dashboard-hero__subtitle">
              Review progress, continue active assessments, and move quickly between reports and inspections.
            </p>
            <div className="dashboard-hero__actions">
              <button type="button" className="dashboard-btn dashboard-btn--primary" onClick={() => navigate('/create-assessment')}>
                New Assessment
              </button>
              <button type="button" className="dashboard-btn dashboard-btn--secondary" onClick={() => navigate('/reports')}>
                View Reports
              </button>
            </div>
          </div>
        </section>

        <section className="dashboard-section">
          <div className="dashboard-section__heading">
            <p className="dashboard-section__eyebrow">Overview</p>
            <h2 className="dashboard-section__title">Security Snapshot</h2>
          </div>

          <div className="dashboard-kpis">
            {kpis.map((kpi) => (
              <article key={kpi.label} className="dashboard-kpi">
                <p className="dashboard-kpi__label">{kpi.label}</p>
                <strong className="dashboard-kpi__value">{kpi.value}</strong>
                <p className="dashboard-kpi__helper">{kpi.helper}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="dashboard-section">
          <div className="dashboard-section__heading">
            <p className="dashboard-section__eyebrow">Workspace</p>
            <h2 className="dashboard-section__title">Activity & Actions</h2>
          </div>

          <div className="dashboard-workspace">
            <article className="dashboard-panel dashboard-panel--primary">
              <div className="dashboard-panel__header">
                <div>
                  <p className="dashboard-panel__eyebrow">Latest Assessment</p>
                  <h3 className="dashboard-panel__title">
                    {lastAssessment ? lastAssessment.name : 'No assessments yet'}
                  </h3>
                </div>
                {lastAssessment && (
                  <span className={`dashboard-status dashboard-status--${lastAssessment.status === 'completed' ? 'complete' : 'active'}`}>
                    {lastAssessment.status === 'completed' ? 'Completed' : 'In Progress'}
                  </span>
                )}
              </div>

              {lastAssessment ? (
                <div className="dashboard-latest">
                  <div className="dashboard-latest__visual">
                    {lastAssessment.score != null ? (
                      <div className="dashboard-latest__gauge">
                        <ScoreGauge value={lastAssessment.score} />
                      </div>
                    ) : (
                      <div className="dashboard-score-placeholder">
                        <span>Assessment</span>
                        <strong>{latestProgress}%</strong>
                      </div>
                    )}
                  </div>

                  <div className="dashboard-latest__content">
                    <p className="dashboard-latest__meta">
                      {lastAssessment.address || 'Location pending'} - {lastAssessment.buildingType || 'Facility review'}
                    </p>
                    <div className="dashboard-progress">
                      <div className="dashboard-progress__row">
                        <span>{latestProgressLabel}</span>
                        <strong>{latestProgress}%</strong>
                      </div>
                      <div className="dashboard-progress__track">
                        <div className="dashboard-progress__fill" style={{ width: `${latestProgress}%` }} />
                      </div>
                      <p className="dashboard-progress__hint">
                        {lastAssessment.status === 'completed'
                          ? 'Report submitted and ready for review.'
                          : 'Save progress anytime and continue later.'}
                      </p>
                    </div>

                    <button
                      type="button"
                      className="dashboard-btn dashboard-btn--solid"
                      onClick={() =>
                        navigate(
                          lastAssessment.status === 'completed'
                            ? `/report/${lastAssessment.id}`
                            : `/assessment?id=${encodeURIComponent(lastAssessment.id)}`
                        )
                      }
                    >
                      {lastAssessment.status === 'completed' ? 'View Report' : 'Continue Assessment'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="dashboard-empty">
                  <p className="dashboard-empty__title">No assessments yet</p>
                  <p className="dashboard-empty__copy">
                    Start your first security walkthrough to generate progress tracking and reports.
                  </p>
                  <button type="button" className="dashboard-btn dashboard-btn--solid" onClick={() => navigate('/create-assessment')}>
                    Create Assessment
                  </button>
                </div>
              )}
            </article>

            <aside className="dashboard-panel dashboard-panel--rail">
              <div className="dashboard-panel__header">
                <div>
                  <p className="dashboard-panel__eyebrow">Quick Actions</p>
                  <h3 className="dashboard-panel__title">What would you like to do?</h3>
                </div>
              </div>

              <div className="dashboard-rail-callout">
                <span className="dashboard-rail-callout__label">Recommended next step</span>
                <strong className="dashboard-rail-callout__value">{nextAction}</strong>
              </div>

              <div className="dashboard-action-list">
                {quickActions.map((action) => (
                  <button
                    key={action.label}
                    type="button"
                    className="dashboard-action"
                    onClick={() => navigate(action.href)}
                  >
                    <span>{action.label}</span>
                    <span aria-hidden>{'>'}</span>
                  </button>
                ))}
              </div>
            </aside>
          </div>
        </section>
      </div>
    </AppShell>
  );
};

export default HomeScreen;


